import { Encoding, EncField, VariableType, MarkType, MarkChannel } from '@mode-vegas/vegas';

import { InterActions } from '@mode/shared/contract-common';
import { EncodingHelpers, RenderDataModel } from '@mode/charts/util-vegas';

import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';

import * as ChartData from './muze-chart-data';
import * as ChartLayers from './muze-chart-layers';
import * as ChartFormat from './muze-chart-format';
import * as ChartTooltip from './muze-chart-tooltip';
import { makeInteractionSideEffect } from './muze-chart-interactions';
import { UpdateFacetSizesFunction } from './muze-chart-format';
import { Muze, MuzeOperators } from './muze-types';
import * as MuzeFeatureFlags from './muze-feature-flag-config';
import { muze } from './muze';

// These are the max limits for rendering
export const MAX_POINTS = 250_000;
export const MAX_SERIES = 12_000;
export const MAX_FACETS = 16_000;
export const MAX_ELEMENT_COUNT = 1_000_000; // High default. This is overridden by the feature flag value.
// Paging limits
export const PAGING_MAX_ROWS_PER_PAGE = 125_000;

export type MuzeCanvas = any;

export type BuildDataAndSchemaResult = {
  dataModel: ChartData.MuzeDataModel;
  schema: ChartData.MuzeDataSchema;
  transform: ChartData.MuzeDatasetTransform | undefined;
  dataStats: VisualizationDataStats;
  muzeVersion: string;
};

/**
 * Rough stats for the expected Muze rendering based on the data returned for the chart.
 */
export type VisualizationDataStats = {
  dataCount?: number;
  schemaCount?: number;
  facetCount?: number;
  elementCount?: number;
  rowCount?: number;
  columnCount?: number;
  rowFacets?: number;
  colFacets?: number;
  measureCount?: number;
  layers?: Array<Record<MarkChannel, { id: string; value: number } | undefined>>;
  discreteCount?: Record<string, number>;
  omittedNullCount?: number;
  loadDataTime?: number;
  requiresRowPaging?: boolean;
  rowPageSize?: number | undefined;
};

export function getMuzeCanvas(canvasRef?: MuzeCanvas): MuzeCanvas {
  if (canvasRef == null) {
    const env = muze();
    return env.canvas();
  }
  return canvasRef;
}

function getMuzeVersion(): string {
  const muze = ChartData.getMuze();
  return muze.version;
}

/**
 * Build the Muze dataset and schema from the RenderDataModel. This function also returns
 * statistics about the size of the data.
 * @param model
 * @param encoding
 * @param format
 * @param fieldFormats
 * @returns Muze dataset and schema which can be used to build the Muze configuration for
 * rendering.
 */
export function buildMuzeDataAndSchema(
  model: RenderDataModel,
  encoding: Encoding,
  format: ChartTypes.ChartFormat,
  fieldFormats: ChartTypes.FieldFormatMap
): BuildDataAndSchemaResult {
  let data: ChartData.MuzeChartSeries;
  switch (encoding.marks.channel) {
    case '*': {
      // this is always a tabular layout
      data = ChartData.makeTabularData(model, encoding, format, fieldFormats);
      break;
    }
    case 'x': {
      // this could be either vertical or variable
      data = ChartData.makeVerticalData(model, encoding, format, fieldFormats);
      break;
    }
    case 'y': {
      // this could be either vertical or variable
      data = ChartData.makeHorizontalData(model, encoding, format, fieldFormats);
      break;
    }
  }
  const { dataModel, schema, transform, stats } = data;

  const calculatedDataStats = ChartData.calculateDataSizes(
    dataModel,
    encoding,
    stats.dataCount,
    stats.dataProperties.discreteCount
  );

  const maxPageSize = MuzeFeatureFlags.getMaxFacetPerPage();
  const pageSize = calculatePageSize(calculatedDataStats, maxPageSize);

  const muzeDataStats: VisualizationDataStats = {
    dataCount: stats.dataCount,
    schemaCount: stats.schemaCount,
    discreteCount: stats.dataProperties.discreteCount,
    omittedNullCount: stats.dataProperties.omittedNullCount,
    loadDataTime: stats.dataProperties.loadTime,
    rowPageSize: pageSize,
    requiresRowPaging: pageSize !== undefined,
    ...calculatedDataStats,
  };

  const muzeVersion = getMuzeVersion();
  return { dataModel, schema, transform, dataStats: muzeDataStats, muzeVersion };
}

/**
 * Configures the Muze canvas based on the specified data, schema and other parameters
 * @returns MuzeCanvas
 */
export function renderMuzeChart(
  model: RenderDataModel,
  encoding: Encoding,
  muzeData: BuildDataAndSchemaResult,
  format: ChartTypes.ChartFormat,
  fieldFormats: ChartTypes.FieldFormatMap,
  fieldColors: ChartTypes.FieldColorMap,
  palette: readonly string[],
  facetSizes: ChartTypes.FacetSizeMap,
  element: HTMLElement,
  token: string,
  canvas: MuzeCanvas,
  eventHandler?: InterActions.Handler,
  updateFacetSizesFunc?: UpdateFacetSizesFunction
): MuzeCanvas {
  if (typeof canvas === 'undefined') {
    throw new Error('Viz Explorer unable to render without canvas');
  }
  // Set the canvas size to prevent redraws later
  canvas.width(element.offsetWidth);
  canvas.height(element.offsetHeight);

  const muze = ChartData.getMuze();

  switch (encoding.marks.channel) {
    case '*': {
      // this is always a tabular layout
      renderWithTabularLayout(model, encoding, muzeData, format, fieldFormats, palette, canvas, muze);
      break;
    }
    case 'x': {
      // this could be either vertical or variable
      renderWithVerticalLayout(model, encoding, muzeData, format, fieldFormats, palette, canvas, muze, token);
      break;
    }
    case 'y': {
      // this could be either vertical or variable
      renderWithHorizontalLayout(model, encoding, muzeData, format, fieldFormats, palette, canvas, muze, token);
      break;
    }
  }
  // Get configurations.
  const legend = ChartFormat.makeLegendFormat(model, encoding, format, fieldColors, palette, fieldFormats);
  const tooltip = ChartTooltip.makeTooltip(model, encoding, fieldFormats);
  const highlight = makeDefaultHighlightInteraction();
  const axes = ChartFormat.makeAxesFormat(model, encoding, fieldFormats, format);
  const rows = ChartFormat.makeRowsConfig(model, encoding, fieldFormats, facetSizes, updateFacetSizesFunc);
  const columns = ChartFormat.makeColumnsConfig(model, encoding, fieldFormats, facetSizes, updateFacetSizesFunc);
  const totals = ChartFormat.makeTotalsFormat(model, muzeData.schema, encoding, fieldFormats);
  Object.assign(rows, totals.rows);
  Object.assign(columns, totals.columns);
  const borders = ChartFormat.makeBordersConfig(model, encoding, rows, columns);

  const usePagination = muzeData.dataStats.requiresRowPaging ?? false;
  const pageSize = muzeData.dataStats.rowPageSize;

  // Assign configurations and render the graph.
  registerClickHandler(token, model, encoding, canvas, eventHandler);
  return canvas
    .color({
      value: palette[0],
    })
    .config({
      autoGroupBy: {
        disabled: true,
      },
      scrollBar: {
        thickness: 7,
        speed: 4,
        buttons: {
          show: false,
        },
      },
      rows,
      columns,
      legend,
      interaction: {
        tooltip,
        highlight: highlight,
      },
      pagination: {
        enabled: usePagination,
        pageSize,
        unionAxisDomainPerPage: false,
      },
      gridLines: {
        color: '#f3f3f3',
      },
      borders,
      axes,
      useUTC: true,
    })
    .mount(element);
}

function renderWithTabularLayout(
  model: RenderDataModel,
  encoding: Encoding,
  muzeData: BuildDataAndSchemaResult,
  format: ChartTypes.ChartFormat,
  fieldFormats: ChartTypes.FieldFormatMap,
  palette: readonly string[],
  canvas: MuzeCanvas,
  muze: Muze
) {
  const layers = ChartLayers.makeTabularLayers(
    model,
    encoding,
    fieldFormats,
    {
      ...format.colors,
    },
    palette
  );
  return render(model, canvas, muzeData, encoding, layers, muze);
}

function renderWithVerticalLayout(
  model: RenderDataModel,
  encoding: Encoding,
  muzeData: BuildDataAndSchemaResult,
  format: ChartTypes.ChartFormat,
  fieldFormats: ChartTypes.FieldFormatMap,
  palette: readonly string[],
  canvas: MuzeCanvas,
  muze: Muze,
  token: string
) {
  const layers = ChartLayers.makeVerticalLayers(
    model,
    encoding,
    muzeData.schema,
    muze.layerFactory,
    fieldFormats,
    { ...format.colors },
    palette,
    token
  );
  return render(model, canvas, muzeData, encoding, layers, muze);
}

function renderWithHorizontalLayout(
  model: RenderDataModel,
  encoding: Encoding,
  muzeData: BuildDataAndSchemaResult,
  format: ChartTypes.ChartFormat,
  fieldFormats: ChartTypes.FieldFormatMap,
  palette: readonly string[],
  canvas: MuzeCanvas,
  muze: Muze,
  token: string
) {
  const layers = ChartLayers.makeHorizontalLayers(
    model,
    encoding,
    muzeData.schema,
    muze.layerFactory,
    fieldFormats,
    { ...format.colors },
    palette,
    token
  );
  return render(model, canvas, muzeData, encoding, layers, muze);
}

function render(
  model: RenderDataModel,
  canvas: MuzeCanvas,
  muzeData: BuildDataAndSchemaResult,
  encoding: Encoding,
  layers: ChartLayers.MuzeLayer[],
  muze: Muze
) {
  canvas.data(muzeData.dataModel);
  if (muzeData.transform != null) {
    canvas.transform(muzeData.transform);
  }
  const rows = buildRows(model, encoding, muze.Operators);
  const columns = buildColumns(model, encoding, muze.Operators);
  const omittedNullCount = muzeData.dataStats.omittedNullCount ?? 0;
  if (omittedNullCount > 0) {
    const nullPluralizedString = omittedNullCount === 1 ? 'NULL OMITTED' : 'NULLS OMITTED';
    canvas.subtitle(`${omittedNullCount} ${nullPluralizedString}`, {
      position: 'bottom',
      align: 'right',
    });
  } else {
    canvas.subtitle(undefined);
  }
  canvas.rows(rows).columns(columns).layers(layers);
}

/**
 * Returns Array of rows for specified encoding.
 *
 * @param {RenderDataModel} model model
 * @param {Encoding} encoding encoding
 * @returns {Array} one- or two-dimensional Array of rows
 */
export function buildRows(model: RenderDataModel, encoding: Encoding, operators: MuzeOperators) {
  const y = makeFieldListMuzeConfig(model, encoding, encoding.row, encoding.y, operators);

  // For dual axes, return two nested Arrays...
  if (encoding.y2.length) {
    // Create a array of dual axis fields which match their relative pair by the index.
    // Ex: if there are three fields on y and the y2 is attached to the second one, then
    // we return an array which is [null, field, null]
    const groupedYMap = buildGroupedFieldList(encoding.y);
    const y2 = Array(groupedYMap.size).fill(null);
    const keys = Array.from(groupedYMap.keys());

    const groupedY2Map = buildGroupedFieldList(encoding.y2);
    for (const fieldListItem of groupedY2Map.values()) {
      const yRef = fieldListItem.fields.length > 0 ? fieldListItem.fields[0].y : undefined;
      if (yRef !== undefined) {
        const index = keys.findIndex((k) => k === yRef);
        if (index >= 0) {
          y2[index] = makeFieldMuzeConfig(model, encoding, fieldListItem, operators);
        }
      }
    }

    return [y, y2];
  }

  // ...or for a pivot table, return one nested Array...
  else if (encoding.marks.series.length < 1 && encoding.marks.properties.type.value === MarkType.Text) {
    return [y];
  }

  // ...otherwise, return a single Array.
  else {
    return y;
  }
}
/**
 * Returns Array of columns for specified encoding.
 *
 * @param {RenderDataModel} model model
 * @param {Encoding} encoding encoding
 * @returns {Array} one- or two-dimensional Array of columns
 */
export function buildColumns(model: RenderDataModel, encoding: Encoding, operators: MuzeOperators) {
  const x = makeFieldListMuzeConfig(model, encoding, encoding.column, encoding.x, operators);

  // For dual axes, return two nested Arrays...
  if (encoding.x2.length) {
    // Create a array of dual axis fields which match their relative pair by the index.
    // Ex: if there are three fields on x and the x2 is attached to the third one, then
    // we return an array which is [null, null, field]

    const groupedXMap = buildGroupedFieldList(encoding.x);
    const x2 = Array(groupedXMap.size).fill(null);
    const keys = Array.from(groupedXMap.keys());

    const groupedX2Map = buildGroupedFieldList(encoding.x2);
    for (const fieldListItem of groupedX2Map.values()) {
      const xRef = fieldListItem.fields.length > 0 ? fieldListItem.fields[0].x : undefined;
      if (xRef !== undefined) {
        const index = keys.findIndex((k) => k === xRef);
        if (index >= 0) {
          x2[index] = makeFieldMuzeConfig(model, encoding, fieldListItem, operators);
        }
      }
    }
    return [x, x2];
  }

  // ...or for a pivot table, return one nested Array...
  else if (encoding.marks.series.length < 1 && encoding.marks.properties.type.value === MarkType.Text) {
    return [x];
  }

  // ...otherwise, return a single Array.
  else {
    return x;
  }
}

export function calculatePageSize(dataStats: VisualizationDataStats, pageSize: number) {
  // Pages are based on number of facets
  // Facets can be small (1 row per facet)
  // Or, large (e.g. all rows in 1 facet)
  // Or, somewhere in between.

  // Paging can only be done if there are multiple row facets available
  // to break. Return undefined if there is only one row facet
  const rowFacetCount = dataStats.rowFacets ?? 1;
  if (rowFacetCount <= 1) {
    return;
  }

  // We are trying to limit the situations where pagination kicks in
  // to only when we are hitting bad rendering performance.
  const rowCount = dataStats.rowCount ?? 1;
  const columnCount = dataStats.columnCount ?? 1;
  const cellCount = rowCount * columnCount;

  // Reduce the limits significantly to a level where we are confident that
  // we can render a page.
  const SAFE_FACETS = Math.min(pageSize, MAX_FACETS);
  const SAFE_MAX_FACETS = MAX_FACETS / 2;
  const SAFE_ELEMENT_COUNT = PAGING_MAX_ROWS_PER_PAGE;
  // Customers generally aren't happy with a small number of rows per page. Set a minimum target
  // row count. We will attempt to show at least this many rows per page if we can do so without
  // exceeding the hard upper limits.
  const MINIMUM_TARGET_ROW_COUNT = 20;

  // If we are well below the facet limit and cell count limit, then return 'undefined' to indicate 'no paging'
  if ((dataStats.facetCount ?? 1) <= SAFE_FACETS && cellCount <= SAFE_ELEMENT_COUNT) {
    return;
  }
  // The full data is large enough to warrant paging. Determine how many row facets
  // should go on a page.

  // Calculate a candidate row facet count that keeps us below the safe facet limit
  const columnFacetCount = dataStats.colFacets ?? 1;
  const measureCount = dataStats.measureCount ?? 1;
  const safePageSizeByFacets = Math.max(1, Math.floor(SAFE_FACETS / (columnFacetCount * measureCount)));

  // Calculate a candidate row facet count based on our max facet limit irrespective of requested facet count
  // We may increase to closer to this if the page size is less than 10
  const safePageSizeByMaxFacets = Math.max(1, Math.floor(SAFE_MAX_FACETS / (columnFacetCount * measureCount)));

  // Calculate a candidate row facet count that keeps us below the safe element limit.
  // The number of rows in each facet can vary and we have no way of knowing. We
  // approximate the number of rows per facet by assuming they are all equal.
  const safeRowsPerPage = Math.max(1, Math.floor(SAFE_ELEMENT_COUNT / columnCount));
  const approxRowsPerFacet = rowCount / rowFacetCount;
  const safePageSizeByRows = Math.max(1, Math.floor(safeRowsPerPage / approxRowsPerFacet));

  // Choose the smaller of the possible page sizes as the biggest we can go per page.
  // In cases where the calculated facet size is less than the minimum target, try to increase up to
  // the target if we still stay below the overall max limit. This ignores the FF setting but provides
  // a better experience for wide tables where the FF limit may result in 1 row per page.
  const maxFacetsPerPage = Math.min(
    safePageSizeByFacets > MINIMUM_TARGET_ROW_COUNT
      ? safePageSizeByFacets
      : Math.min(MINIMUM_TARGET_ROW_COUNT, safePageSizeByMaxFacets),
    safePageSizeByRows
  );

  // Figure out how many total pages that page size will generate
  const numberOfPages = Math.ceil(rowFacetCount / maxFacetsPerPage);

  // Now equalize the number of facets per page. Since our page size is typically
  // large we want to avoid having a small remainder on the last page. An extreme
  // example would be a page size of 2000 when there are 2001 total row facets.
  const equalizedPageSize = Math.max(1, Math.ceil(rowFacetCount / numberOfPages));

  // For page sizes ~100 or larger, ceiling to next 10 to look better
  return equalizedPageSize <= 95 ? equalizedPageSize : Math.ceil(equalizedPageSize / 10) * 10;
}

interface FieldListItem<T extends EncField> {
  type: 'field' | 'group';
  fields: Array<T>;
}

/**
 * Creates a list of FieldListItems which represents the
 * EncFields in the passed array. All fields which are part of the same
 * group are aggregated into a single object. Single fields are
 * included as themselves.
 */
function buildGroupedFieldList<T extends EncField>(fields: ReadonlyArray<T>) {
  return fields.reduce((mapped, field) => {
    if (field.groupId != null) {
      if (!mapped.has(field.groupId)) {
        mapped.set(field.groupId, { type: 'group', fields: [] });
      }
      const mapping = mapped.get(field.groupId);
      mapping?.fields.push(field);
    } else if (field.id != null) {
      mapped.set(field.id, { type: 'field', fields: [field] });
    }
    return mapped;
  }, new Map<string, FieldListItem<T>>());
}

/**
 * Returns an array of Muze field configuration objects. This can be single
 * fields and/or shared fields.
 */
function makeFieldListMuzeConfig(
  model: RenderDataModel,
  encoding: Encoding,
  facetFields: ReadonlyArray<EncField>,
  axisFields: ReadonlyArray<EncField>,
  operators: MuzeOperators
) {
  const groupedFieldList = buildGroupedFieldList([...facetFields, ...axisFields]);

  return Array.from(groupedFieldList.values()).map((fieldListItem) =>
    makeFieldMuzeConfig(model, encoding, fieldListItem, operators)
  );
}

/**
 * Builds a Muze field configuration object for either a single field or
 * for a shared field.
 */
function makeFieldMuzeConfig<T extends EncField>(
  model: RenderDataModel,
  encoding: Encoding,
  fieldListItem: FieldListItem<T>,
  operators: MuzeOperators
) {
  if (fieldListItem.type === 'field') {
    return encFieldsToMuzeConfig(model, encoding, fieldListItem.fields[0], operators);
  } else {
    const groupFields = fieldListItem.fields.map((field) => field.id ?? '') ?? [];

    // Check if any of these fields are also part of a range
    const allSharedIds = new Set<string>(groupFields);
    groupFields.forEach((fieldId) => {
      const baselineFieldId = ChartFormat.getBaselineFieldIdForSeries(encoding, fieldId);
      if (baselineFieldId != null) {
        // Create a shared field which is composed of the baseline field and the series field
        allSharedIds.add(baselineFieldId);
      }
    });

    return allSharedIds.size === 1
      ? encFieldsToMuzeConfig(model, encoding, fieldListItem.fields[0], operators)
      : operators.share(...Array.from(allSharedIds));
  }
}

function registerClickHandler(
  token: string,
  model: RenderDataModel,
  encoding: Encoding,
  canvas: MuzeCanvas,
  handler?: InterActions.Handler
) {
  const { VolatileBehaviour } = muze.Behaviours.standards;

  muze.ActionModel.for(canvas)
    // Register a new behavioural action which will select single item
    // Extending Volatile Behaviour will select only the recent interacted plot.
    .registerBehaviouralActions(
      class SingleSelectBehaviour extends VolatileBehaviour {
        static formalName() {
          return 'singleSelect';
        }
      }
    )
    // Map doubleclick action with singleselect behaviour so that whenever we doubleclick on the bars singleSelect
    // behaviour gets dispatched.
    .registerPhysicalBehaviouralMap({
      click: {
        behaviours: ['singleSelect'],
      },
    })
    .dissociateBehaviour(['select', 'click'])
    .dissociateBehaviour(['brush', 'drag'])
    .registerPhysicalActions({
      click: (firebolt: any) => (targetEl: any) => {
        targetEl.on('click', function (this: any, data: any) {
          const event = muze.utils.getEvent();
          const mousePos = muze.utils.getClientPoint(this, event);
          const payload = firebolt.getPayloadFromEvent('click', mousePos, { data, event });

          firebolt.triggerPhysicalAction('click', payload);

          if (payload.target) {
            const point = firebolt.context.getMarkInfFromLayers(mousePos.x, mousePos.y, {});

            if (point) {
              const { id, layerId } = point;
              const layerInst = firebolt.context.layers().find((layer: any) => layer.id() === layerId);

              // Create an object from the identifiers values
              const idObj = id[0].reduce((acc: any, val: any, i: number) => {
                acc[val] = id[1][i];
                return acc;
              }, {});

              // This returns array of points of the layers
              const points = layerInst._points.flat();

              // Find the point
              const clickedPoint = points.find((p: any) => {
                const data = p.data;
                let found = true;
                for (const key in idObj) {
                  found = found && idObj[key] === data[key];
                }
                return found;
              });

              const data = clickedPoint.data;
              // Color can either be encoded as 'backgroundColor' or 'color'. We do not support
              // both. Check to see if we have a field defined for background, else default to 'color'.
              const colorChannel =
                layerInst.config().encoding?.backgroundColor?.field != null ? 'backgroundColor' : 'color';
              // Get the id of the field associated with color
              const colorField: string = layerInst.config().encoding?.[colorChannel]?.field;
              // Check if a splitBy field is defined for the color channel. If so, the color axis is
              // actually an Object keyed by the measure name. The values are the 'axes' associated
              // with each measure.
              const colorLegendSplitByFieldId: string | undefined | null =
                layerInst.config().encoding?.[colorChannel]?.splitBy;

              // The color axes contains all of the color information from Muze
              let colorAxis = layerInst.axes()?.[colorChannel];
              if (colorAxis != null && colorLegendSplitByFieldId != null) {
                // If we are splitting, get the color 'axes' for this measure
                const splitValue = data[colorLegendSplitByFieldId];
                if (Object.prototype.hasOwnProperty.call(colorAxis, splitValue)) {
                  colorAxis = colorAxis[splitValue];
                }
              }

              const yField: string = layerInst.config().encoding.y.field;
              const xField: string = layerInst.config().encoding.x.field;
              const xValue: string | number = data[xField];
              const yValue: string | number = data[yField];

              let primaryFieldResult: FlamingoTypes.ModeField | undefined;
              let primaryValue: number | string;
              let secondaryFieldResult: FlamingoTypes.ModeField | undefined;
              let secondaryValue: number | string;

              switch (encoding.marks.channel) {
                case 'y': {
                  const xEncField = EncodingHelpers.getEncFieldById(encoding, xField);
                  primaryFieldResult = model.makeField(xEncField);
                  const yEncField = EncodingHelpers.getEncFieldById(encoding, yField);
                  secondaryFieldResult = yEncField && model.makeField(yEncField);
                  primaryValue = xValue;
                  secondaryValue = yValue;
                  break;
                }
                case 'x': {
                  const yEncField = EncodingHelpers.getEncFieldById(encoding, yField);
                  primaryFieldResult = model.makeField(yEncField);
                  const xEncField = EncodingHelpers.getEncFieldById(encoding, xField);
                  secondaryFieldResult = xEncField && model.makeField(xEncField);
                  primaryValue = yValue;
                  secondaryValue = xValue;
                  break;
                }
                case '*': {
                  const xEncField = EncodingHelpers.getEncFieldById(encoding, xField);
                  primaryFieldResult = model.makeField(xEncField);
                  const yEncField = EncodingHelpers.getEncFieldById(encoding, yField);
                  secondaryFieldResult = yEncField && model.makeField(yEncField);
                  primaryValue = xValue;
                  secondaryValue = yValue;
                  break;
                }
              }

              const columns: Array<ChartTypes.Channel> = [];
              const seriesName = colorField ? data[colorField] : secondaryFieldResult ? secondaryFieldResult.name : '';

              const colorValue = colorAxis?.getColor(seriesName);
              const facetValues = firebolt.context.facetFieldsMap();

              // Add primary and secondary axes, if any.
              if (primaryFieldResult) {
                columns.push({
                  name: primaryFieldResult.name,
                  value: primaryValue,
                  id: primaryFieldResult.alias,
                });
              }
              if (secondaryFieldResult) {
                columns.push({
                  name: secondaryFieldResult.name,
                  value: secondaryValue,
                  id: secondaryFieldResult.alias,
                });
              }

              // Add additional facets.
              Object.keys(facetValues).forEach((id) => {
                const facetEncField = EncodingHelpers.getEncFieldById(encoding, id);
                const facetFieldResult = model.makeField(facetEncField);
                if (facetFieldResult?.name && !columns.some((column) => column.name === facetFieldResult.name)) {
                  columns.push({
                    name: facetFieldResult.name,
                    value: facetValues[id],
                    id: id,
                  });
                }
              });

              const addSeriesField = (fieldId: string) => {
                const encField = EncodingHelpers.getEncFieldById(encoding, fieldId);
                const modeField = model.makeField(encField);
                const value = data[fieldId];
                if (modeField?.name) {
                  columns.push({
                    name: modeField.name,
                    value,
                    id: fieldId,
                  });
                }
              };
              if (colorField) {
                addSeriesField(colorField);
              }
              const sizeField: string = layerInst.config().encoding.size.field;
              if (sizeField) {
                addSeriesField(sizeField);
              }
              const detailFields: string = layerInst.config().encoding.detail.field;
              if (detailFields && detailFields.length) {
                addSeriesField(detailFields[0]);
              }
              const textField: string = layerInst.config().encoding.text.field;
              if (textField) {
                addSeriesField(textField);
              }
              // Make sure all of the x and y values are included if they are in the data
              if (encoding.marks.channel !== '*') {
                const channelFields =
                  encoding.marks.channel === 'x' ? [...encoding.y, ...encoding.y2] : [...encoding.x, ...encoding.x2];
                channelFields.forEach((field) => {
                  const modeField = model.makeField(field);
                  if (modeField != null && !columns.some((column) => column.name === modeField.name)) {
                    columns.push({
                      name: modeField.name,
                      value: data[field.id ?? ''],
                      id: field.id,
                    });
                  }
                });
              }

              const isWrappedValue = (value: unknown) =>
                Object.prototype.toString.call(value) === '[object Object]' &&
                Object.prototype.hasOwnProperty.call(value, '_value');

              const normalizedColumns = columns.map((col) => {
                // Wrapped value a-la Muze. Unwrap it
                if (isWrappedValue(col.value)) {
                  const value = (col.value as any)._value;
                  return Object.assign({}, col, {
                    value: String(value) === 'null' ? null : value,
                  });
                }
                return col;
              });

              handler &&
                handler({
                  event,
                  target: {
                    id: token,
                    columns: normalizedColumns,
                    series: {
                      name: seriesName,
                      id: data[ChartData.SID_FIELD],
                      color: {
                        name: seriesName,
                        color: colorValue,
                        value: seriesName,
                      },
                    },
                    type: ChartTypes.TargetType.VegasChart,
                  },
                  position: {
                    clientX: event.clientX,
                    clientY: event.clientY,
                  },
                  type: InterActions.Type.MarkClick,
                });
            }
          }
        });
      },
    });
}

function makeDefaultHighlightInteraction() {
  return makeInteractionSideEffect({ crossline: { enabled: false } });
}

function encFieldsToMuzeConfig(
  model: RenderDataModel,
  encoding: Encoding,
  enc: EncField,
  operators: MuzeOperators
): ChartData.MuzeFieldConfig {
  if (!enc.id) {
    throw new Error('Unknown field in config');
  }

  // Check if this field is a series field with a baseline.
  const baselineFieldId = ChartFormat.getBaselineFieldIdForSeries(encoding, enc);
  if (baselineFieldId != null) {
    // Create a shared field which is composed of the baseline field and the series field
    const composed = operators.share(baselineFieldId, enc.id);
    return composed;
  }

  return {
    field: enc.id,
    as:
      enc.variable.type === VariableType.DISCRETE ||
      ChartData.treatContinuousDatePartAsDiscrete(model, encoding, model.makeField(enc))
        ? 'discrete'
        : 'continuous',
  };
}
